// TODO: not an ideal solution for tracking as a sophisticated system
// session and events need a much comprehensive design from product + eng side for it to be scalable and robust
import { useCallback, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { v4 as uuid4 } from 'uuid';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useSenderStore } from '@/hooks/useSenderStore';
import { API_BASE_URL } from '@/utils/apiClient';
import i18n from '@/utils/i18n';
import { getApiUrlByLocation } from '@/utils/apiClient';
import { useSender } from './useSender';
import { useWidgetConfig } from './useWidgetConfig';
import useWidgetDataAttr from './useWidgetDataAttr';

interface Session {
  id: string;
  startedAt: number | null;
  endedAt: number | null;
  landingPage: {
    url: string;
    title: string;
  } | null;
  dropoffPage: {
    url: string;
    title: string;
  } | null;
}

interface SessionStore {
  session: Session | null;
  actions: {
    validateAndSetSession: () => void;
    setSessionDropoff: () => void;
    getStorageKey: (widgetConfigId: string, companyId: string) => string;
  };
}

const SESSION_EXPIRE_DURATION = 24 * 60 * 60 * 1000; // 24 hrs
const PAGE_VISITED_EVENT_NAME = 'PAGE_VISITED';

const useSessionStore = create<SessionStore>()(
  persist(
    (set, get) => ({
      session: null,

      actions: {
        validateAndSetSession: () => {
          const currentSession = get().session;
          const now = Date.now();

          if (
            !currentSession ||
            (currentSession.endedAt &&
              now - currentSession.endedAt > SESSION_EXPIRE_DURATION)
          ) {
            // expired or new session
            const newSession: Session = {
              id: uuid4(),
              startedAt: now,
              endedAt: null,
              landingPage: {
                url: window.location.href,
                title: document.title,
              },
              dropoffPage: null,
            };

            set({ session: newSession });
          }
        },

        setSessionDropoff: () => {
          const currentSession = get().session;

          if (!currentSession) return;

          const updatedSession: Session = {
            ...currentSession,
            endedAt: Date.now(),
            dropoffPage: {
              url: window.location.href,
              title: document.title,
            },
          };

          set({ session: updatedSession });
        },

        getStorageKey: (widgetConfigId: string, companyId: string) => {
          return `sf-live-chat-session-${widgetConfigId}-${companyId}`;
        },
      },
    }),
    {
      name: 'sf-live-chat-session',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ session: state.session }),
    },
  ),
);

const useSessionStoreInit = () => {
  const { companyId } = useWidgetDataAttr();
  const { config: widgetConfig } = useWidgetConfig();
  const {
    actions: { validateAndSetSession, getStorageKey },
  } = useSessionStore();

  const initializeSessionStore = useCallback(async () => {
    const storageKey = getStorageKey(
      widgetConfig?.channelIdentityId || '',
      companyId || '',
    );

    if (!widgetConfig?.channelIdentityId || !companyId) {
      return;
    }

    if (useSessionStore.persist.getOptions().name !== storageKey) {
      useSessionStore.persist.setOptions({
        name: storageKey,
      });

      await useSessionStore.persist.rehydrate();
    }

    validateAndSetSession();
  }, [getStorageKey, widgetConfig, companyId, validateAndSetSession]);

  // initialize storage key when widget config is available
  useEffect(() => {
    initializeSessionStore();
  }, [initializeSessionStore]);
};

let globalPageVisitedStartedAt: number | null = null;
let globalReferralPage: {
  url: string;
  title: string;
} | null = null;

export const useTracking = () => {
  const { config } = useWidgetConfig();
  const { location } = useWidgetDataAttr();
  const { senderId } = useSender();
  const { session } = useSessionStore();
  const channelIdentityId = config?.channelIdentityId;

  const firePageVisitedEventMutation = useMutation({
    mutationFn: async () => {
      if (!senderId || !channelIdentityId || !session) return;

      const baseUrlByLocation = getApiUrlByLocation(location);
      const baseUrl = baseUrlByLocation || API_BASE_URL;
      const url = `${baseUrl}/LiveChatV2/Public/Senders/${senderId}/Tracking`;
      const body = JSON.stringify({
        name: PAGE_VISITED_EVENT_NAME,
        senderId,
        channelIdentityId,
        startedAt: globalPageVisitedStartedAt
          ? new Date(globalPageVisitedStartedAt).toISOString()
          : null,
        endedAt: new Date().toISOString(),
        metadata: {
          visitedPage: {
            url: window.location.href,
            title: document.title,
          },
          referralPage: globalReferralPage,
          session: {
            id: session.id,
            startedAt: session.startedAt
              ? new Date(session.startedAt).toISOString()
              : null,
            endedAt: session.endedAt
              ? new Date(session.endedAt).toISOString()
              : null,
            landingPage: session.landingPage,
            dropoffPage: session.dropoffPage,
          },
          lang: i18n.language,
        },
      });

      let sendBeaconQueued = false;

      // As sendBeacon does not support request headers, baseUrlByLocation is required
      // to send to correct data center
      if (typeof navigator.sendBeacon === 'function' && baseUrlByLocation) {
        const blob = new Blob([body], { type: 'application/json' });
        sendBeaconQueued = navigator.sendBeacon(url, blob);
      }

      if (!sendBeaconQueued) {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const signature = useSenderStore.getState().signature;

        if (signature) {
          headers['X-Sleekflow-LiveChat-Signature'] = signature;
        }

        if (location) {
          headers['X-Sleekflow-Location'] = location;
        }

        fetch(url, {
          method: 'POST',
          headers,
          body,
          keepalive: true,
        }).catch(() => {});
      }
    },
  });

  const setReferralPage = useCallback(() => {
    globalReferralPage = {
      url: window.location.href,
      title: document.title,
    };
  }, []);

  return {
    firePageVisitedEventMutation,
    setReferralPage,
  };
};

export const useTrackingInit = () => {
  useSessionStoreInit();
  const {
    actions: { setSessionDropoff },
  } = useSessionStore();
  const { firePageVisitedEventMutation } = useTracking();

  const handlePageHideEvent = useCallback(() => {
    setSessionDropoff();
    firePageVisitedEventMutation.mutate();
  }, [firePageVisitedEventMutation, setSessionDropoff]);

  useEffect(() => {
    globalPageVisitedStartedAt = Date.now();
  }, []);

  useEffect(() => {
    // TODO: listen for pagehide event for more cases covered (mostly in mobile)
    // but it will likely not working well with sendbeacon, need to verify
    // https://developer.chrome.com/docs/web-platform/page-lifecycle-api#developer-recommendations-for-each-state
    window.addEventListener('pagehide', handlePageHideEvent);
    return () => {
      window.removeEventListener('pagehide', handlePageHideEvent);
    };
  }, [handlePageHideEvent]);
};
